import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../assets/clienttickets.module.css";
import { downloadCSV, downloadPDF, printTable } from "../utils/exportUtils";

// Column definitions for the tickets table
const allColumns = [
  { key: "ticketId", label: "ID" },
  { key: "statusName", label: "Status" },
  { key: "propertyName", label: "Property" },
  { key: "created_at", label: "Submitted" },
  { key: "date_estimated_completion", label: "Deadline" },
  { key: "date_completed", label: "Completed" },
  { key: "job_type", label: "Job type" },
  { key: "cost_of_works_formatted", label: "Cost of works" },
  { key: "description", label: "Description" },
  { key: "actions", label: "Actions" },
];

const ClientTickets = () => {
  const { user } = useAuth();
  const { id: clientIdFromUrl } = useParams(); // Get client ID from URL
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10, // Match Laravel default
  });
  const [filters, setFilters] = useState({
    date_type: "",
    date: "",
    date_to: "",
    status_id: "All",
    property_type: "", // Empty string for "Active only" (default), "all" for "All"
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map(col => col.key)
  );
  const [showColDropdown, setShowColDropdown] = useState(false);

  const fetchTickets = useCallback(async () => {
    // Use client ID from URL if available, otherwise fall back to user context
    let clientId = clientIdFromUrl;

    // If no client ID in URL, get from user context (for backward compatibility)
    if (!clientId && user) {
      // Support all userable_type formats for client contacts:
      // - 'clientscontacts' (production database format)
      // - 'ClientsContact', 'ClientContact' (alternative formats)
      // - 'App\\Clients\\ClientContact' (Laravel namespace format)
      // - 'App\\ClientsContacts\\ClientsContact' (legacy format)
      const clientContactTypes = [
        'clientscontacts',
        'ClientsContact',
        'ClientContact',
        'App\\Clients\\ClientContact',
        'App\\ClientsContacts\\ClientsContact'
      ];

      if (clientContactTypes.includes(user?.userable_type)) {
        // Fetch the client contact to get the parent client_id
        const token = localStorage.getItem("token");
        const contactResponse = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/clientscontacts/${user.userable_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (contactResponse.ok) {
          const contactData = await contactResponse.json();
          clientId = contactData.client_id;
        }
      } else {
        clientId = user?.userable_id || user?.client_id;
      }
    }

    if (!clientId) {
      console.error("No client ID found");
      setTickets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Match Laravel API endpoint: POST /api/tickets/clients/{clientId}
      const apiUrl = `${
        process.env.REACT_APP_API_URL || "http://localhost:3001"
      }/api/tickets/clients/${clientId}`;

      // Build DataTables-style parameters
      const formData = new FormData();
      formData.append('draw', '1');
      formData.append('start', (pagination.currentPage - 1) * entriesPerPage);
      formData.append('length', entriesPerPage);
      formData.append('order[0][column]', '2'); // Sort by created_at desc (Laravel default)
      formData.append('order[0][dir]', 'desc');

      // Add search parameter if present
      if (searchQuery) {
        formData.append('search[value]', searchQuery);
      }

      // Add filter parameters
      console.log("🔎 Applying filters to API call:", JSON.stringify(filters));

      if (filters.status_id && filters.status_id !== "All") {
        console.log("  ✓ Adding status_id:", filters.status_id);
        formData.append('status_id', filters.status_id);
      }
      if (filters.property_type) {
        console.log("  ✓ Adding property_type:", filters.property_type);
        formData.append('property_type', filters.property_type);
      }
      if (filters.date_type) {
        console.log("  ✓ Adding date_type:", filters.date_type);
        formData.append('date_type', filters.date_type);
      }
      if (filters.date) {
        console.log("  ✓ Adding date:", filters.date);
        formData.append('date', filters.date);
      }
      if (filters.date_to) {
        console.log("  ✓ Adding date_to:", filters.date_to);
        formData.append('date_to', filters.date_to);
      }

      console.log("📤 Sending API request to:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Tickets data:", data);
        setTickets(data.data || []);
        setPagination(prev => ({
          ...prev,
          totalRecords: data.recordsTotal || 0,
          totalPages: Math.ceil((data.recordsTotal || 0) / entriesPerPage)
        }));
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch tickets:", response.status, errorText);
        setTickets([]);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [user, pagination.currentPage, entriesPerPage, refreshTrigger]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    console.log("🔍 Search clicked with filters:", filters);
    console.log("🔍 Search query:", searchQuery);

    // Reset to first page and trigger refresh
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    // Trigger refresh by incrementing the trigger
    // This will cause fetchTickets to run with current filters and searchQuery
    setRefreshTrigger(prev => prev + 1);
  };

    const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    // Fetch tickets will be triggered by useEffect when pagination changes
  };

  // Utility functions matching Laravel formatting
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // dd/mm/yyyy format
  };

  // Status label component to replace HTML rendering
  const StatusLabel = ({ statusName, statusColor }) => (
    <span 
      className={styles.statusLabel}
      style={{ backgroundColor: statusColor }}
    >
      {statusName}
    </span>
  );

  // Property link component to replace HTML rendering
  const PropertyLink = ({ propertyId, propertyName }) => (
    <span 
      className={styles.propertyLink}
      onClick={() => navigate(`/properties/${propertyId}`)}
      style={{ fontWeight: 700 }}
    >
      {propertyName}
    </span>
  );

  // Prepare export data with only visible columns (exclude actions column)
  const getExportData = () => {
    return tickets.map(ticket => {
      const row = {};
      visibleColumns.forEach(colKey => {
        // Skip actions column in export
        if (colKey === 'actions') return;
        const col = allColumns.find(c => c.key === colKey);
        if (col) {
          let value = ticket[colKey];
          // Format dates for export
          if (colKey === 'created_at' || colKey === 'date_estimated_completion' || colKey === 'date_completed') {
            value = formatDate(value);
          }
          row[col.label] = value || '';
        }
      });
      return row;
    });
  };

  // Export functions
  const handleExportCSV = () => {
    if (!tickets || tickets.length === 0) {
      alert('No data available to export as CSV');
      return;
    }
    const exportData = getExportData();
    downloadCSV(exportData, 'client-tickets.csv');
  };

  const handleExportPDF = () => {
    if (!tickets || tickets.length === 0) {
      alert('No data available to export as PDF');
      return;
    }
    const exportData = getExportData();
    downloadPDF(exportData, 'client-tickets.pdf', null, 'Maintenance Tickets Report');
  };

  const handlePrint = () => {
    if (!tickets || tickets.length === 0) {
      alert('No data available to print');
      return;
    }
    const exportData = getExportData();
    printTable(exportData, null, 'Maintenance Tickets');
  };

  const handleColumnVisibility = (e) => {
    e.stopPropagation();
    setShowColDropdown(prev => !prev);
  };

  const handleColDropdownBlur = (e) => {
    // Only close if clicking outside the entire dropdown container
    const relatedTarget = e.relatedTarget;
    if (!e.currentTarget.contains(relatedTarget)) {
      setShowColDropdown(false);
    }
  };

  const toggleColumnVisibility = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    setVisibleColumns((cols) =>
      cols.includes(colKey)
        ? cols.filter((c) => c !== colKey)
        : [...cols, colKey]
    );
  };

  // Fetch available statuses on component mount
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/tickets/filter-data`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.statuses) {
            setStatuses(data.statuses);
          }
        }
      } catch (error) {
        console.error("Error fetching statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.message}>Loading tickets...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumb}>
          <span className={styles.breadcrumbTag}>TICKETS</span>
        </div>

        {/* Main Title */}
        <div className={styles.header}>
          <h1 className={styles.title}>Maintenance</h1>
        </div>

        {/* Filters Section - Matching Laravel structure */}
        <div className={styles.filtersSection}>
          <form 
            onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
            className={styles.searchForm}
          >
            <fieldset className={styles.formHorizontal}>
              <div className={styles.filterRow}>
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Date</label>
                  <select
                    value={filters.date_type}
                    onChange={(e) => handleFilterChange("date_type", e.target.value)}
                    className={styles.filterInput}
                  >
                    <option value="">Select date</option>
                    <option value="submitted">Submitted</option>
                    <option value="deadline">Deadline</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>From</label>
                  <div className={styles.dateInputWrapper}>
                    <input
                      type="date"
                      value={filters.date}
                      onChange={(e) => handleFilterChange("date", e.target.value)}
                      className={styles.dateInput}
                    />
                    <span className={styles.calendarIcon}>📅</span>
                  </div>
                </div>

                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>To</label>
                  <div className={styles.dateInputWrapper}>
                    <input
                      type="date"
                      value={filters.date_to}
                      onChange={(e) => handleFilterChange("date_to", e.target.value)}
                      className={styles.dateInput}
                    />
                    <span className={styles.calendarIcon}>📅</span>
                  </div>
                </div>
              </div>

              <div className={styles.filterRow}>
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Status</label>
                  <select
                    value={filters.status_id}
                    onChange={(e) => handleFilterChange("status_id", e.target.value)}
                    className={styles.filterInput}
                  >
                    <option value="All">All</option>
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Property Type</label>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="property_type"
                        value="all"
                        checked={filters.property_type === "all"}
                        onChange={(e) =>
                          handleFilterChange("property_type", e.target.value)
                        }
                      />
                      <span>All</span>
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="property_type"
                        value=""
                        checked={filters.property_type === ""}
                        onChange={(e) =>
                          handleFilterChange("property_type", e.target.value)
                        }
                      />
                      <span>Active only</span>
                    </label>
                  </div>
                </div>

                <div className={styles.searchButtonContainer}>
                  <button type="submit" className={styles.searchButton}>
                    Search
                  </button>
                </div>
              </div>
            </fieldset>
          </form>
        </div>

        {/* Results Section - DataTable matching Laravel */}
        <div className={styles.resultsSection}>
          {/* DataTable Toolbar - Match Laravel structure */}
          <div className={styles.dtToolbar}>
            <div className={styles.dtToolbarLeft}>
              <label className={styles.dtLabel}>
                Show{" "}
                <select
                  value={entriesPerPage}
                  onChange={(e) => {
                    setEntriesPerPage(Number(e.target.value));
                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                  className={styles.dtSelect}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="30">30</option>
                  <option value="50">50</option>
                  <option value="75">75</option>
                  <option value="100">100</option>
                </select>{" "}
                entries
              </label>
            </div>

            {/* Export buttons matching Laravel */}
            <div className={styles.dtToolbarButtons}>
              <button className={styles.exportBtn} onClick={handleExportCSV}>
                Export as CSV
              </button>
              <button className={styles.exportBtn} onClick={handleExportPDF}>
                Export as PDF
              </button>
              <button className={styles.exportBtn} onClick={handlePrint}>
                Print table
              </button>
              <div
                style={{ position: 'relative', display: 'inline-block' }}
                onBlur={handleColDropdownBlur}
                tabIndex={-1}
              >
                <button
                  className={styles.exportBtn}
                  onClick={handleColumnVisibility}
                  tabIndex={0}
                >
                  Column visibility
                </button>
                {showColDropdown && (
                  <div
                    className={styles.columnVisibilityDropdown}
                    onMouseDown={(e) => e.preventDefault()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      padding: '10px',
                      zIndex: 1000,
                      minWidth: '200px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      marginTop: '4px'
                    }}
                  >
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>
                      Toggle Columns:
                    </h4>
                    {allColumns.map((col) => (
                      <label
                        key={col.key}
                        onClick={(e) => toggleColumnVisibility(e, col.key)}
                        style={{
                          display: 'block',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontSize: '13px'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(col.key)}
                          onChange={() => {}}
                          style={{ marginRight: '8px', pointerEvents: 'none' }}
                        />
                        <span>{col.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.dtToolbarRight}>
              <label className={styles.dtLabel}>
                Search:{" "}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  className={styles.dtSearchInput}
                  placeholder=""
                />
              </label>
            </div>
          </div>

          {tickets && tickets.length > 0 ? (
            <>
              <div className={styles.dataTableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr style={{ fontSize: '11px' }}>
                      {allColumns.map(col =>
                        visibleColumns.includes(col.key) && (
                          <th
                            key={col.key}
                            className={col.key === 'created_at' || col.key === 'date_estimated_completion' || col.key === 'date_completed' ? styles.tooltip : ''}
                            title={
                              col.key === 'created_at' ? 'Date when submitted' :
                              col.key === 'date_estimated_completion' ? 'Proposed deadline' :
                              col.key === 'date_completed' ? 'Date when completed' : ''
                            }
                          >
                            {col.label}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={visibleColumns.length} className={styles.loadingCell}>
                          Loading tickets...
                        </td>
                      </tr>
                    ) : (
                      tickets.map((ticket) => (
                        <tr key={ticket.ticketId}>
                          {allColumns.map(col =>
                            visibleColumns.includes(col.key) && (
                              <td
                                key={col.key}
                                className={col.key === 'description' ? styles.descriptionCell : ''}
                              >
                                {col.key === 'statusName' ? (
                                  <StatusLabel
                                    statusName={ticket.statusName}
                                    statusColor={ticket.statusColor}
                                  />
                                ) : col.key === 'propertyName' ? (
                                  <PropertyLink
                                    propertyId={ticket.propertyId}
                                    propertyName={ticket.propertyName}
                                  />
                                ) : col.key === 'created_at' || col.key === 'date_estimated_completion' || col.key === 'date_completed' ? (
                                  formatDate(ticket[col.key])
                                ) : col.key === 'actions' ? (
                                  <button
                                    className="btn btn-sm btn-warning"
                                    onClick={() => navigate(`/tickets/${ticket.ticketId}`)}
                                    style={{
                                      padding: '4px 8px',
                                      fontSize: '12px',
                                      lineHeight: '1.5',
                                      borderRadius: '3px',
                                      border: '1px solid transparent',
                                      cursor: 'pointer',
                                      backgroundColor: '#f0ad4e',
                                      color: '#fff',
                                      borderColor: '#eea236'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#ec971f';
                                      e.target.style.borderColor = '#d58512';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = '#f0ad4e';
                                      e.target.style.borderColor = '#eea236';
                                    }}
                                  >
                                    Show
                                  </button>
                                ) : (
                                  ticket[col.key]
                                )}
                              </td>
                            )
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination - Match Laravel DataTables */}
              <div className={styles.dtFooter}>
                <div className={styles.paginationInfo}>
                  Showing {Math.min(((pagination.currentPage - 1) * entriesPerPage) + 1, pagination.totalRecords)} to {Math.min(pagination.currentPage * entriesPerPage, pagination.totalRecords)} of {pagination.totalRecords} entries
                </div>
                <div className={styles.paginationControls}>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage <= 1}
                    className={`${styles.paginationBtn} ${pagination.currentPage <= 1 ? styles.disabled : ''}`}
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`${styles.paginationBtn} ${pagination.currentPage === pageNum ? styles.active : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage >= pagination.totalPages}
                    className={`${styles.paginationBtn} ${pagination.currentPage >= pagination.totalPages ? styles.disabled : ''}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.message}>
              {loading ? "Loading tickets..." : "No tickets!"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientTickets;
